import type {
  Alignment,
  CompatibilityResult,
  Question,
  QuestionScore,
  ResultCategory,
  ResultTheme,
  ThemeGroup,
  VisitorAnswer,
} from './types';

/**
 * ===========================================================================
 * THE COMPATIBILITY MODEL
 * ===========================================================================
 *
 * Simple on purpose. It is a weighted average of hand-authored alignment
 * judgements, with two hard gates. That is the whole thing.
 *
 * It is NOT validated, NOT predictive and NOT science. It is a structured way
 * of noticing where two people's stated preferences overlap, and it is only as
 * good as the alignment values written in the content file. The UI is written
 * to reflect that honestly — it never shows a number.
 *
 * ---------------------------------------------------------------------------
 * HOW A SINGLE QUESTION IS SCORED
 * ---------------------------------------------------------------------------
 *
 * `single` / `scenario`
 *   The chosen option declares its own alignment with the creator's answer.
 *   Authored by hand, because "is this close?" is a judgement call.
 *
 * `scale`
 *   Alignment is derived from the distance between the two values:
 *     0 apart -> exact     1 apart -> close
 *     2 apart -> neutral   3+      -> different
 *
 * `multi`
 *   How much of the visitor's selection the creator also chose
 *   (shared ÷ how many she picked):
 *     >= 0.60 -> exact     >= 0.35 -> close
 *     >= 0.15 -> neutral   else    -> different
 *   Measured against her selection, not the two sets combined, so that ticking
 *   everything still cannot game its way to a perfect score — the more she
 *   picks, the more of it has to land — while picking fewer things is not
 *   itself treated as disagreement.
 *
 *   This used to be Jaccard (shared ÷ combined), which quietly punished the
 *   shorter answer: "a run, hike, walk or day outdoors" against a Saturday
 *   that contains exactly that plus three other things scored 1 ÷ 4 = 0.25 and
 *   was read out under "where we differ". Everything she said, he had said
 *   too. There was no difference there to report.
 *
 * ---------------------------------------------------------------------------
 * HOW THEY COMBINE
 * ---------------------------------------------------------------------------
 *
 *   score = Σ(weight × alignmentScore) ÷ Σ(weight)      over answered questions
 *
 * Unanswered questions are excluded from both sums rather than counted as
 * zero, so an abandoned-then-resumed journey is not silently penalised.
 *
 * ---------------------------------------------------------------------------
 * THE TWO GATES
 * ---------------------------------------------------------------------------
 *
 * A question may declare a `nonNegotiable`. Selecting one of its conflict
 * options forces `different-paths` regardless of everything else. Exactly two
 * questions do this — children and monogamy — because those are the two James
 * actually named. It is a gate, not a penalty: no amount of overlap elsewhere
 * changes it, and no other question can trigger it.
 *
 * ---------------------------------------------------------------------------
 * THE CEILING
 * ---------------------------------------------------------------------------
 *
 * Separately from the gates, an unresolved weight-5 core question caps the
 * result at `worth-a-conversation`. "A serious relationship, and we let the
 * future unfold" plus "I could see children with the right person" is a
 * perfectly good pair of answers, and it is not agreement about children or
 * about where this is going. Announcing "there may be something worth
 * exploring here" over the top of it would be the site telling somebody it
 * heard a yes.
 *
 * The ceiling is not arithmetic — a weighted average cannot express "these two
 * questions have to be actually answered, not nearly answered" without giving
 * them a weight that would distort everything else.
 *
 * ---------------------------------------------------------------------------
 * SCORING IS NOT PRESENTATION
 * ---------------------------------------------------------------------------
 *
 * `ALIGNMENT_SCORES` decides the number. `groupFor` decides which list an
 * answer is read out under, and it is a different judgement: `close` is worth
 * 0.72 whatever the question, but on children it belongs under "worth talking
 * about" and on a Saturday it belongs under "where we align".
 */

/* ---------------------------------------------------------------------------
   Tunables. All in one place, all documented.
   REVIEW (James): these are the dials. Changing them changes who gets which
   result. The defaults were chosen to be generous in the middle — it is much
   worse to wrongly tell someone "different paths" than to wrongly invite a
   conversation.
   --------------------------------------------------------------------------- */

export const ALIGNMENT_SCORES: Record<Alignment, number> = {
  exact: 1,
  close: 0.72,
  neutral: 0.45,
  different: 0.1,
};

export const CATEGORY_THRESHOLDS = {
  /** At or above this weighted score -> strong potential. */
  strongPotential: 0.75,
  /** At or above this -> worth a conversation. Below it -> different paths. */
  worthAConversation: 0.5,
} as const;

/** How many themes to surface on the result page. Keep them small and readable. */
export const THEME_LIMITS = {
  alignment: 3,
  conversation: 2,
  difference: 2,
  /** The optional round's own list, built by the result page. */
  everyday: 3,
} as const;

/**
 * The weight at which a core question stops being a data point and starts
 * being the thing being asked. Only `intentions`, `children` and `monogamy`
 * reach it, and that is deliberate — see `THE CEILING` above.
 */
export const DECISIVE_WEIGHT = 5;

const SCALE_DISTANCE_ALIGNMENT: Alignment[] = ['exact', 'close', 'neutral'];

const MULTI_OVERLAP_BANDS: Array<{ min: number; alignment: Alignment }> = [
  { min: 0.6, alignment: 'exact' },
  { min: 0.35, alignment: 'close' },
  { min: 0.15, alignment: 'neutral' },
];

/* ---------------------------------------------------------------------------
   Per-question alignment
   --------------------------------------------------------------------------- */

/**
 * Works out how closely one visitor answer sits to the creator's answer for
 * the same question. Exported because it is the most useful thing to test.
 *
 * Returns `null` when the answer cannot be compared at all — a malformed or
 * stale saved answer, for example. Callers skip those rather than guessing.
 */
export function alignmentFor(
  question: Question,
  answer: VisitorAnswer,
): Alignment | null {
  if (answer.questionId !== question.id) return null;

  switch (answer.kind) {
    case 'single': {
      if (question.creatorAnswer.kind !== 'single') return null;
      const option = question.options?.find((o) => o.id === answer.optionId);
      if (!option) return null;
      // An option with no declared alignment is treated as a neutral
      // difference rather than throwing: better a slightly soft result than a
      // blank page because someone added an option and forgot the field.
      return option.alignment ?? 'neutral';
    }

    case 'multi': {
      if (question.creatorAnswer.kind !== 'multi') return null;
      const mine = new Set(question.creatorAnswer.optionIds);
      const theirs = new Set(answer.optionIds);
      if (mine.size === 0 || theirs.size === 0) return 'different';

      let shared = 0;
      for (const id of theirs) if (mine.has(id)) shared += 1;
      // Share of *her* selection, not of the two sets combined. A subset of the
      // creator's answer is agreement, not a difference; see the note on
      // `multi` at the top of this file.
      const overlap = shared / theirs.size;

      for (const band of MULTI_OVERLAP_BANDS) {
        if (overlap >= band.min) return band.alignment;
      }
      return 'different';
    }

    case 'scale': {
      if (question.creatorAnswer.kind !== 'scale') return null;
      const distance = Math.abs(question.creatorAnswer.value - answer.value);
      return SCALE_DISTANCE_ALIGNMENT[distance] ?? 'different';
    }

    default:
      return null;
  }
}

function isNonNegotiableConflict(
  question: Question,
  answer: VisitorAnswer,
): boolean {
  if (!question.nonNegotiable) return false;
  // Only single-choice questions carry gates today. Guarding here rather than
  // in the type keeps the content format simple.
  if (answer.kind !== 'single') return false;
  return question.nonNegotiable.conflictOptionIds.includes(answer.optionId);
}

/* ---------------------------------------------------------------------------
   Presentation: which list an answer is read out under
   --------------------------------------------------------------------------- */

/**
 * One of the three questions where a near-miss is a conversation rather than
 * an agreement: children, intention, commitment.
 */
export function isDecisiveCore(question: Question): boolean {
  return question.section === 'core' && question.weight >= DECISIVE_WEIGHT;
}

/**
 * A decisive question that was answered with something other than the same
 * answer. Not a conflict — just the thing that has to be talked about rather
 * than assumed.
 */
export function isUnresolvedCore(question: Question, alignment: Alignment): boolean {
  return isDecisiveCore(question) && alignment !== 'exact';
}

/**
 * Which list on the result page this answer belongs in.
 *
 * The rule the whole redesign turns on. Before it, every `close` answer was
 * filed under "Where we align", which meant somebody could say "a serious
 * relationship, and we let the future unfold" and "I could see children with
 * the right person" and be told those were two things we agreed on. They are
 * not; they are two things to talk about, and the page now says so.
 *
 * Outside the core tier `close` really is alignment. Preferring a lively
 * dinner to a quiet one is not an open question about a life.
 */
export function groupFor(question: Question, alignment: Alignment): ThemeGroup {
  if (alignment === 'neutral' || alignment === 'different') return 'differ';
  if (alignment === 'exact') return 'align';
  return question.section === 'core' ? 'conversation' : 'align';
}

/* ---------------------------------------------------------------------------
   Categorisation
   --------------------------------------------------------------------------- */

export function categorise(
  score: number,
  hasNonNegotiableConflict: boolean,
  hasUnresolvedCoreQuestion = false,
): ResultCategory {
  if (hasNonNegotiableConflict) return 'different-paths';
  if (score >= CATEGORY_THRESHOLDS.strongPotential) {
    // The ceiling. A high average cannot outvote an unanswered question about
    // children — it can only mean the rest of the week would be pleasant.
    return hasUnresolvedCoreQuestion ? 'worth-a-conversation' : 'strong-potential';
  }
  if (score >= CATEGORY_THRESHOLDS.worthAConversation) return 'worth-a-conversation';
  return 'different-paths';
}

/* ---------------------------------------------------------------------------
   Theme selection
   --------------------------------------------------------------------------- */

/** Sorts by weight, then by score, so the most consequential themes surface. */
export function byImportance(a: QuestionScore, b: QuestionScore): number {
  if (b.weight !== a.weight) return b.weight - a.weight;
  return b.score - a.score;
}

/** The same, but heaviest *and lowest scoring* first: differences, worst first. */
export function byGravity(a: QuestionScore, b: QuestionScore): number {
  if (b.weight !== a.weight) return b.weight - a.weight;
  return a.score - b.score;
}

/**
 * The sentence shown on the result page for one question.
 *
 * The per-alignment override wins; otherwise it falls back to whichever of the
 * question's two general strings suits the group the answer landed in.
 *
 * The fallback used to key off alignment alone, which is why "I could see it
 * with the right person" was reported as "we both want parenthood to be part
 * of our future" — `close` and `exact` shared one sentence written for
 * `exact`. Authoring a `notes.close` opts a question out of that individually;
 * keying the fallback off `groupFor` means a core question nobody has got
 * round to authoring one for still cannot claim an agreement by default.
 */
export function noteFor(question: Question, alignment: Alignment): string {
  const override = question.notes?.[alignment];
  if (override) return override;
  return groupFor(question, alignment) === 'align'
    ? question.alignmentNote
    : question.conversationPrompt;
}

/**
 * Turns a run of scores into the lines the result page reads out, in the order
 * given and no more than `limit` of them.
 *
 * Exported because the result page builds one list of its own — the everyday
 * overlap found in the optional round — and that list is defined by which
 * round a question sits in, which is knowledge this file deliberately does not
 * have.
 */
export function themesFor(
  scores: QuestionScore[],
  questions: Question[] | Map<string, Question>,
  limit: number,
): ResultTheme[] {
  const byId =
    questions instanceof Map ? questions : new Map(questions.map((q) => [q.id, q]));

  const themes: ResultTheme[] = [];
  for (const s of scores) {
    const question = byId.get(s.questionId);
    if (!question) continue;
    themes.push({
      questionId: s.questionId,
      theme: s.theme,
      section: question.section,
      note: noteFor(question, s.alignment),
    });
    if (themes.length >= limit) break;
  }
  return themes;
}

/* ---------------------------------------------------------------------------
   The entry point
   --------------------------------------------------------------------------- */

/**
 * Scores a set of visitor answers against a creator's questions.
 *
 * Pure: same inputs, same output, no I/O, no clock, no randomness. This runs
 * in the browser and the answers never leave it unless the visitor explicitly
 * chooses to send them.
 */
export function scoreCompatibility(
  questions: Question[],
  answers: Record<string, VisitorAnswer>,
): CompatibilityResult {
  const breakdown: QuestionScore[] = [];
  const nonNegotiableConflicts: string[] = [];

  let weightedTotal = 0;
  let weightSum = 0;
  let hasUnresolvedCoreQuestion = false;

  for (const question of questions) {
    const answer = answers[question.id];
    if (!answer) continue;

    const alignment = alignmentFor(question, answer);
    if (alignment === null) continue;

    const conflict = isNonNegotiableConflict(question, answer);
    if (conflict && question.nonNegotiable) {
      nonNegotiableConflicts.push(question.nonNegotiable.conflictSummary);
    }

    if (isUnresolvedCore(question, alignment)) hasUnresolvedCoreQuestion = true;

    const score = ALIGNMENT_SCORES[alignment];
    const weight = Math.max(0, question.weight);

    weightedTotal += score * weight;
    weightSum += weight;

    breakdown.push({
      questionId: question.id,
      theme: question.theme,
      weight,
      section: question.section,
      alignment,
      group: groupFor(question, alignment),
      score,
      isNonNegotiableConflict: conflict,
    });
  }

  const score = weightSum === 0 ? 0 : weightedTotal / weightSum;
  const hasNonNegotiableConflict = nonNegotiableConflicts.length > 0;
  const category = categorise(score, hasNonNegotiableConflict, hasUnresolvedCoreQuestion);

  const questionsById = new Map(questions.map((q) => [q.id, q]));
  const inGroup = (group: ThemeGroup) => breakdown.filter((s) => s.group === group);

  return {
    category,
    score,
    alignmentThemes: themesFor(
      inGroup('align').sort(byImportance),
      questionsById,
      THEME_LIMITS.alignment,
    ),
    conversationThemes: themesFor(
      inGroup('conversation').sort(byImportance),
      questionsById,
      THEME_LIMITS.conversation,
    ),
    differenceThemes: themesFor(
      inGroup('differ').sort(byGravity),
      questionsById,
      THEME_LIMITS.difference,
    ),
    hasNonNegotiableConflict,
    hasUnresolvedCoreQuestion,
    nonNegotiableConflicts,
    breakdown,
    answeredCount: breakdown.length,
    totalCount: questions.length,
  };
}
